import { signalStore, withState, withMethods, patchState, withComputed } from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { AuthService } from './auth.service';
import { ChangePasswordViewModel, CredentialsViewModel, RegisterAgencyUserViewModel, RegisterWholesalerViewModel, SelfUserUpdateREQ, TwoFactorCredentialsViewModel, UnregisteredUserViewModel, UserViewModel } from '@program-suite/data-common';
import { CredentialsWith2FaViewModel, VerifyLoginResponse } from '@program-suite/data-common/program';
import { UserService } from './user.service';

interface AuthState {
  username: string | null;
  token: string | null; // JWT token from login2fa
  isEmail2faPending: boolean; // True after validateCredentials, before login2fa
  isAuthenticated: boolean; // True only after successful 2FA
  user: UserViewModel | null;
  requiresApp2fa: boolean;
  verifyLoginRes: VerifyLoginResponse | null;
  portal: 'program' | 'wholesaler' | null;
  unregisteredUser: UnregisteredUserViewModel | null;
}

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState<AuthState>({
    username: null,
    token: null,
    isEmail2faPending: false,
    isAuthenticated: false,
    user: null,
    unregisteredUser: null,
    requiresApp2fa: false,
    verifyLoginRes: null,
    portal: null,
  }),
  withComputed((state) => ({
    debug: computed(() => ({
      username: state.username(),
      token: state.token(),
      isAuthenticated: state.isAuthenticated(),
      user: state.user(),
      isEmail2faPending: state.isEmail2faPending(),
      requiresApp2fa: state.requiresApp2fa(),
      verifyLoginRes: state.verifyLoginRes(),
    })),
    isWholesale: computed(() => {
      return (
        state.isAuthenticated() &&
        (state.user()?.roles?.includes('Wholesale Admin') || state.user()?.roles?.includes('Wholesale Manager'))
      );
    }),
    isAdmin: computed(() => {
      // make sure roles is lower case
      const roles = state.user()?.roles?.map((role) => role.toLowerCase()) ?? [];
      return roles?.includes('admin');
    }),
  })),
  withMethods((store, authService = inject(AuthService), userService = inject(UserService)) => ({
    // Step 1: Validate credentials (username/password)
    async validateCredentials(credentials: CredentialsViewModel) {
      await authService.validateCredentials(credentials);
      patchState(store, {
        username: credentials.userName,
        isEmail2faPending: true,
      });
    },
    // Step 2: Complete login with 2FA
    async login2fa(credentials: TwoFactorCredentialsViewModel) {
      const token = await authService.login2fa(credentials);
      localStorage.setItem('W_TOKEN', token);
      const user = await userService.getUser();
      patchState(store, {
        token,
        isAuthenticated: true,
        user: user,
        isEmail2faPending: false,
      });
    },
    logout() {
      localStorage.clear();
      const rootUrl = `${window.location.origin}/`;
      window.location.href = rootUrl;
      patchState(store, {
        username: null,
        token: null,
        isAuthenticated: false,
        user: null,
        isEmail2faPending: false,
        requiresApp2fa: false,
      });
    },
    async loadFromStorage() {
      const token = localStorage.getItem('W_TOKEN');
      if (token) {
        const user = await userService.getUser();
        patchState(store, {
          token,
          isAuthenticated: true,
          user: user,
          isEmail2faPending: false,
        });
      } else {
        patchState(store, {
          username: null,
          token: null,
          isAuthenticated: false,
          user: null,
          isEmail2faPending: false,
        });
      }
    },
    async getUnregisteredUser(userId: string): Promise<UnregisteredUserViewModel> {
     const user = await userService.getUnregisteredUser(userId);
      patchState(store, {
        unregisteredUser: user,
      }); 

      return user;
    },

    //Program methods
    async verifyLogin(credentials: CredentialsViewModel) {
      const res = await authService.verifyLogin(credentials);
      patchState(store, {
        username: credentials.userName,
        verifyLoginRes: res,
        isEmail2faPending: true,
      });
    },
    async loginApp2fa(credentials: CredentialsWith2FaViewModel) {
      const token = await authService.loginApp2fa(credentials);
      localStorage.setItem('W_TOKEN', token);
      const user = await userService.getUser();
      patchState(store, {
        token,
        isAuthenticated: true,
        user: user,
        isEmail2faPending: false,
      });
    },
    setApp2faStatus(required: boolean) {
      patchState(store, {
        requiresApp2fa: required,
      });
    },
    setAppPortal(portal: 'program' | 'wholesaler' | null) {
      patchState(store, {
        portal: portal,
      });
    },
    async registerWholesaler(model: RegisterWholesalerViewModel): Promise<void> {
      return await authService.registerWholesaler(model);
    },
    async registerAgencyUser(model: RegisterAgencyUserViewModel): Promise<void> {
      return await authService.registerAgencyUser(model);
    },
    async updateUserProfile(model: SelfUserUpdateREQ) {
      const success = await userService.updateUserProfile(model);
      if (success) {
        patchState(store, {
          user: { ...store.user(), ...model },
        });
      }
    },
    async changePassword(model: ChangePasswordViewModel) {
      await authService.changePassword(model);
    },
    async resendCode(credentials: CredentialsViewModel) {
      await authService.resendCode(credentials);
    }
  })),
);
